import { Product } from '@shared/components/ui/Product';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { ChevronLeft, ChevronRight, Pencil, Search } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProfitabilityProductCost, ProfitabilityProductWithoutCost } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import { formatProductCostEffectiveFrom, groupProductCostsByProduct } from '../../utils/productCostsDisplay';
import type { ProductCostsModalProduct } from '../interaction/ProductCostsModal';

export type ProductCostsListView = 'pending' | 'configured';

interface ProductCostsListProps {
  view: ProductCostsListView;
  onViewChange: (view: ProductCostsListView) => void;
  search: string;
  onSearchChange: (value: string) => void;
  pendingTotal?: number;
  pendingRows: ProfitabilityProductWithoutCost[];
  configuredRows: ProfitabilityProductCost[];
  isLoading: boolean;
  isError?: boolean;
  locale: string;
  currency: string;
  baseCostLabel: string;
  pagination?: {
    currentPage: number;
    lastPage: number;
    total: number;
    onPrev: () => void;
    onNext: () => void;
    isFetching: boolean;
  };
  onManageProduct: (product: ProductCostsModalProduct) => void;
}

function FilterTab({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

export const ProductCostsList = ({
  view,
  onViewChange,
  search,
  onSearchChange,
  pendingTotal,
  pendingRows,
  configuredRows,
  isLoading,
  isError,
  locale,
  currency,
  baseCostLabel,
  pagination,
  onManageProduct,
}: ProductCostsListProps) => {
  const { t } = useTranslation('profitabilityModule');
  const configuredGroups = useMemo(() => groupProductCostsByProduct(configuredRows), [configuredRows]);
  const rowCount = view === 'pending' ? pendingRows.length : configuredGroups.length;
  const colCount = view === 'pending' ? 5 : 4;
  const showPagination = view === 'pending' && pagination && (pagination.total > 0 || pagination.lastPage > 1);

  const emptyMessage =
    view === 'pending'
      ? search.trim()
        ? t('product_costs.empty_pending_search')
        : t('product_costs.empty_pending')
      : search.trim()
        ? t('product_costs.empty_configured_search')
        : t('product_costs.empty_configured');

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
          <FilterTab
            active={view === 'pending'}
            onClick={() => onViewChange('pending')}
            testId="profitability-product-costs-view-pending"
          >
            {t('product_costs.view_pending')}
            {pendingTotal != null && pendingTotal > 0 ? ` (${pendingTotal})` : ''}
          </FilterTab>
          <FilterTab
            active={view === 'configured'}
            onClick={() => onViewChange('configured')}
            testId="profitability-product-costs-view-configured"
          >
            {t('product_costs.view_configured')}
          </FilterTab>
        </div>

        <div className="w-full space-y-1.5 sm:max-w-xs">
          <Label className="sr-only">{t('product_costs.search')}</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t('product_costs.search_placeholder')}
              className="border-border bg-background pl-9"
              data-testid="profitability-product-costs-search"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground">{t('product_costs.col_product')}</TableHead>
              {view === 'pending' && (
                <TableHead className="text-right text-muted-foreground">
                  {t('product_costs.col_orders_count')}
                </TableHead>
              )}
              <TableHead className="text-right text-muted-foreground">{t('product_costs.col_cost')}</TableHead>
              <TableHead className="text-muted-foreground">{t('product_costs.col_effective_from')}</TableHead>
              <TableHead className="w-[120px] text-muted-foreground">{t('product_costs.col_actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="border-border">
                  <TableCell colSpan={colCount}>
                    <div className="h-10 animate-pulse rounded bg-muted/60" />
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {t('product_costs.list_error')}
                </TableCell>
              </TableRow>
            )}

            {!isLoading && !isError && rowCount === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} className="py-10 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              view === 'pending' &&
              pendingRows.map((row) => (
                <TableRow key={row.id} className="border-border">
                  <TableCell>
                    <Product
                      id={row.id}
                      name={row.name}
                      code={row.code ?? undefined}
                      src={row.image_url?.trim() ? row.image_url : ''}
                      className="min-w-0 max-w-full justify-start"
                      showTooltip
                      showImageModal={Boolean(row.image_url?.trim())}
                      data-testid={`profitability-product-cost-pending-${row.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {new Intl.NumberFormat(locale).format(row.orders_count ?? 0)}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">—</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t('product_costs.status_pending')}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 border-border"
                      onClick={() =>
                        onManageProduct({
                          id: row.id,
                          name: row.name,
                          code: row.code,
                          image_url: row.image_url,
                        })
                      }
                      data-testid={`profitability-product-cost-manage-${row.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {t('product_costs.manage')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}

            {!isLoading &&
              !isError &&
              view === 'configured' &&
              configuredGroups.map((group) => {
                const primaryCost = group.costs[0];
                const costSummary =
                  group.costs.length === 1
                    ? formatProfitabilityAmount(primaryCost.cost, locale, currency)
                    : t('product_costs.costs_count', { count: group.costs.length });

                const effectiveSummary =
                  group.costs.length === 1
                    ? formatProductCostEffectiveFrom(primaryCost.effective_from, locale, baseCostLabel)
                    : t('product_costs.multiple_costs');

                return (
                  <TableRow key={group.productId} className="border-border">
                    <TableCell className="max-w-[320px]">
                      <Product
                        id={group.productId}
                        name={group.productName}
                        code={group.productCode ?? undefined}
                        src=""
                        className="min-w-0 max-w-full justify-start"
                        showTooltip
                        data-testid={`profitability-product-cost-product-${group.productId}`}
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm font-medium">{costSummary}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{effectiveSummary}</TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 border-border"
                        onClick={() =>
                          onManageProduct({
                            id: group.productId,
                            name: group.productName,
                            code: group.productCode,
                            image_url: null,
                          })
                        }
                        data-testid={`profitability-product-cost-manage-${group.productId}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {t('product_costs.manage')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>

      {showPagination && pagination && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">{t('pagination.total', { total: pagination.total })}</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {t('pagination.page', { current: pagination.currentPage, last: pagination.lastPage })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={pagination.currentPage <= 1 || pagination.isFetching}
              onClick={pagination.onPrev}
              title={t('pagination.prev')}
              data-testid="profitability-product-costs-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8 border-border"
              disabled={pagination.currentPage >= pagination.lastPage || pagination.isFetching}
              onClick={pagination.onNext}
              title={t('pagination.next')}
              data-testid="profitability-product-costs-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
